import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  centsToDollars,
  errorMessage,
  MAX_DESCRIPTION_LENGTH,
  SERVICE_CATEGORIES,
  URGENCIES,
  useRfq,
  useRfqImageDelete,
  useRfqImageUpload,
  useRfqInvitations,
  useRfqInvite,
  useRfqMatches,
  useRfqNote,
  useRfqSelectQuote,
  useRfqStatus,
  useRfqUpdate,
  type CustomerContact,
  type InviteContractorResult,
  type JobProjection,
  type QuoteProjection,
  type RfqDetail as RfqDetailData,
  type RfqImage,
  type RfqInvitationSummary,
  type RfqLocation,
  type ServiceCategory,
  type UpdateRfqInput,
  type Urgency,
} from "../api/rfqs.js";
import { MIN_SEARCH_LENGTH, useContractorSearch } from "../api/contractors.js";
import { useOpsMe } from "../api/me.js";

/**
 * RFQ detail + support actions (ops customers section). Shows the request,
 * the customer's contact details, photos, Q&A, and the contractor-side
 * roll-up (quotes / selection / jobs) the customer system projects — the
 * lifecycle a support person needs. Triage status is admin-only; notes are
 * admin|support.
 *
 * Staff also correct requests here: customers phone in with "I picked the
 * wrong category" or send photos to support instead of uploading them, and
 * until now the only fix was to ask them to redo it. Editing is admin|support
 * and stops once the request leaves triage — a closed or cancelled request is
 * history, not a draft.
 */
export function RfqDetail() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const { data, isPending, isError } = useRfq(rfqId);
  const { data: me } = useOpsMe();
  const roles = me?.roles ?? [];
  const isAdmin = roles.includes("admin");
  const canTriage = isAdmin || roles.includes("support");
  const status = useRfqStatus(rfqId!);
  const note = useRfqNote(rfqId!);
  const [noteText, setNoteText] = useState("");

  if (isPending) return <p className="text-sm text-charcoal">Loading…</p>;
  if (isError || !data) {
    return (
      <div>
        <BackLink />
        <p className="mt-4 text-sm text-charcoal">Couldn&rsquo;t load this request.</p>
      </div>
    );
  }

  const { rfq, customer, quotes, selection, jobs } = data;
  const contactName = [customer?.givenName, customer?.familyName].filter(Boolean).join(" ");
  const canEdit = canTriage && (rfq.status === "SUBMITTED" || rfq.status === "IN_REVIEW");
  const stillOpenForQuotes =
    (rfq.status === "SUBMITTED" || rfq.status === "IN_REVIEW") && selection?.status !== "SELECTED";

  return (
    <div className="max-w-3xl">
      <BackLink />
      <div className="mt-2 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-charcoal">
            {categoryLabel(rfq.serviceCategory)} request
          </h1>
          <p className="text-sm text-charcoal">
            {rfq.location.city}, {rfq.location.state} {rfq.location.postalCode} ·{" "}
            {urgencyLabel(rfq.urgency)}
          </p>
        </div>
        <span className="rounded-full bg-field-gray px-3 py-1 text-xs font-semibold text-charcoal">
          {rfq.status.replace("_", " ")}
        </span>
      </div>

      <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-charcoal">
        {rfq.description}
      </p>

      <ContactCard customer={customer} customerSub={rfq.customerSub} contactName={contactName} />

      <JobLocationCard location={rfq.location} />

      {canEdit && <EditRequestCard rfqId={rfqId!} rfq={rfq} />}

      {(rfq.images.length > 0 || canEdit) && (
        <PhotosCard rfqId={rfqId!} images={rfq.images} canEdit={canEdit} />
      )}

      {(rfq.questions ?? []).length > 0 && (
        <Card title="Questions & answers">
          <ul className="space-y-2 text-sm">
            {rfq.questions!.map((q) => (
              <li key={q.questionId}>
                <p className="font-medium text-charcoal">{q.question}</p>
                <p className="text-charcoal">{q.answer ?? "(unanswered)"}</p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <QuotesCard
        rfqId={rfqId!}
        quotes={quotes}
        selection={selection}
        canAccept={isAdmin && stillOpenForQuotes}
      />

      {canTriage && <InvitedCard rfqId={rfqId!} />}

      {canTriage && stillOpenForQuotes && (
        <FindProCard
          rfqId={rfqId!}
          category={rfq.serviceCategory}
          postalCode={rfq.location.postalCode}
          photoCount={rfq.images.length}
          canInvite={isAdmin}
        />
      )}

      {isAdmin && stillOpenForQuotes && <InviteContractorCard rfqId={rfqId!} />}

      <FeedbackCard feedback={data.feedback} jobs={jobs} />

      {jobs.length > 0 && (
        <Card title="Job status">
          {jobs.map((j) => (
            <p key={j.jobId} className="text-sm text-charcoal">
              {j.status}
              {j.scheduledFor ? ` · scheduled ${j.scheduledFor}` : ""}
            </p>
          ))}
        </Card>
      )}

      <Card title="Triage notes">
        {(rfq.adminNotes ?? []).length === 0 ? (
          <p className="text-sm text-charcoal">No notes yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {rfq.adminNotes!.map((n) => (
              <li key={n.id} className="text-charcoal">
                <span className="text-charcoal">{new Date(n.createdAt).toLocaleString()}</span> ·{" "}
                {n.authorSub} — {n.text}
              </li>
            ))}
          </ul>
        )}
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const t = noteText.trim();
            if (t) note.mutate(t, { onSuccess: () => setNoteText("") });
          }}
        >
          <input
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a triage note…"
            className="flex-1 rounded-lg border border-charcoal/30 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={note.isPending || noteText.trim().length === 0}
            className="rounded-lg border border-charcoal/30 px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            Add note
          </button>
        </form>
      </Card>

      {isAdmin && <TriageStatusCard rfq={rfq} status={status} />}
    </div>
  );
}

/**
 * Move the request through its lifecycle.
 *
 * Cancelling and reopening ask for a reason first. Both are the transitions
 * someone questions afterwards — "why was this called off?", "who reopened
 * it?" — and the reason is stored as a triage note, which is now the request's
 * only readable history.
 */
function TriageStatusCard({
  rfq,
  status,
}: {
  rfq: RfqDetailData["rfq"];
  status: ReturnType<typeof useRfqStatus>;
}) {
  const [confirming, setConfirming] = useState<"CANCELLED" | "IN_REVIEW" | null>(null);
  const [reason, setReason] = useState("");
  const isOpen = rfq.status === "SUBMITTED" || rfq.status === "IN_REVIEW";
  const isTerminal = rfq.status === "CLOSED" || rfq.status === "CANCELLED";

  function commit(next: "IN_REVIEW" | "CLOSED" | "CANCELLED", note?: string) {
    status.mutate(
      { status: next, ...(note?.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => {
          setConfirming(null);
          setReason("");
        },
      },
    );
  }

  return (
    <Card title="Triage status">
      {confirming ? (
        <div className="space-y-2">
          <label htmlFor="status-reason" className="block text-xs font-medium text-charcoal">
            {confirming === "CANCELLED"
              ? "Why is this being cancelled? (saved as a triage note)"
              : "Why is this being reopened? (saved as a triage note)"}
          </label>
          <input
            id="status-reason"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              confirming === "CANCELLED" ? "Customer phoned in to cancel" : "Closed by mistake"
            }
            className={FIELD_CLASS}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={status.isPending}
              onClick={() => commit(confirming, reason)}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {confirming === "CANCELLED" ? "Cancel request" : "Reopen request"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(null);
                setReason("");
              }}
              className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal"
            >
              Never mind
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rfq.status === "SUBMITTED" && (
            <button
              type="button"
              disabled={status.isPending}
              onClick={() => commit("IN_REVIEW")}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Mark in review
            </button>
          )}
          {isOpen && (
            <>
              <button
                type="button"
                disabled={status.isPending}
                onClick={() => commit("CLOSED")}
                className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal disabled:opacity-50"
              >
                Close request
              </button>
              <button
                type="button"
                disabled={status.isPending}
                onClick={() => setConfirming("CANCELLED")}
                className="rounded-lg border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 disabled:opacity-50"
              >
                Cancel request
              </button>
            </>
          )}
          {isTerminal && (
            <button
              type="button"
              disabled={status.isPending}
              onClick={() => setConfirming("IN_REVIEW")}
              className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal disabled:opacity-50"
            >
              Reopen request
            </button>
          )}
        </div>
      )}
      {status.error && <p className="mt-2 text-sm text-rose-700">{errorMessage(status.error)}</p>}
    </Card>
  );
}

const FIELD_CLASS = "w-full rounded-lg border border-charcoal/30 px-3 py-2 text-sm text-charcoal";

/**
 * Correct the request on the customer's behalf. Only the fields the customer
 * could have got wrong are editable — location and property are identity, not
 * detail. We PATCH just what changed so two staffers editing different fields
 * don't overwrite each other.
 */
function EditRequestCard({ rfqId, rfq }: { rfqId: string; rfq: RfqDetailData["rfq"] }) {
  const update = useRfqUpdate(rfqId);
  const [serviceCategory, setServiceCategory] = useState(rfq.serviceCategory);
  const [urgency, setUrgency] = useState(rfq.urgency);
  const [description, setDescription] = useState(rfq.description);

  // The record refetches under an open form all the time: adding a photo or a
  // triage note on this same page invalidates the detail query, and another
  // staffer may be editing concurrently. Adopt the server's values only while
  // this form is untouched — someone mid-sentence must never lose what they
  // typed to a background refetch that didn't even change these fields.
  const [synced, setSynced] = useState({
    serviceCategory: rfq.serviceCategory,
    urgency: rfq.urgency,
    description: rfq.description,
  });
  if (
    rfq.serviceCategory !== synced.serviceCategory ||
    rfq.urgency !== synced.urgency ||
    rfq.description !== synced.description
  ) {
    const untouched =
      serviceCategory === synced.serviceCategory &&
      urgency === synced.urgency &&
      description === synced.description;
    setSynced({
      serviceCategory: rfq.serviceCategory,
      urgency: rfq.urgency,
      description: rfq.description,
    });
    if (untouched) {
      setServiceCategory(rfq.serviceCategory);
      setUrgency(rfq.urgency);
      setDescription(rfq.description);
    }
  }

  const trimmed = description.trim();
  const changes: UpdateRfqInput = {};
  if (serviceCategory !== rfq.serviceCategory) {
    changes.serviceCategory = serviceCategory as ServiceCategory;
  }
  if (urgency !== rfq.urgency) changes.urgency = urgency as Urgency;
  if (trimmed !== rfq.description) changes.description = trimmed;
  const isDirty = Object.keys(changes).length > 0;

  function reset() {
    setServiceCategory(rfq.serviceCategory);
    setUrgency(rfq.urgency);
    setDescription(rfq.description);
    update.reset();
  }

  return (
    <Card title="Edit request">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (isDirty && trimmed.length > 0) update.mutate(changes);
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="edit-category" className="block text-xs font-medium text-charcoal">
              Service category
            </label>
            <select
              id="edit-category"
              value={serviceCategory}
              onChange={(e) => setServiceCategory(e.target.value)}
              className={`mt-1 ${FIELD_CLASS}`}
            >
              {SERVICE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-urgency" className="block text-xs font-medium text-charcoal">
              Urgency
            </label>
            <select
              id="edit-urgency"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              className={`mt-1 ${FIELD_CLASS}`}
            >
              {URGENCIES.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="edit-description" className="block text-xs font-medium text-charcoal">
            Description
          </label>
          {/* Same cap the server enforces, so a long paste is trimmed at the
              keyboard instead of coming back as a validation error. */}
          <textarea
            id="edit-description"
            rows={4}
            maxLength={MAX_DESCRIPTION_LENGTH}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`mt-1 ${FIELD_CLASS}`}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={update.isPending || !isDirty || trimmed.length === 0}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Save changes
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={update.isPending}
            className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
        {update.error && <p className="text-sm text-rose-700">{errorMessage(update.error)}</p>}
      </form>
    </Card>
  );
}

/**
 * Invite a pro who ISN'T in the registry yet (admin only, while the request
 * is still open for quotes). Pros who are already on file get invited from
 * the Find a pro card above; this is the path that creates a record, so it
 * asks for the business name the registry needs.
 *
 * The contractor system mints the same magic link the automatic fan-out
 * sends, emails it to the pro, and returns it here for on-screen copy/share.
 * A business name is only needed when the email isn't in the registry yet —
 * the server answers 400 with exactly that message otherwise.
 */
function InviteContractorCard({ rfqId }: { rfqId: string }) {
  const invite = useRfqInvite(rfqId);
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState<InviteContractorResult | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    invite.mutate(
      {
        email: trimmedEmail,
        ...(businessName.trim() ? { businessName: businessName.trim() } : {}),
        ...(contactName.trim() ? { contactName: contactName.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      },
      { onSuccess: (r) => setResult(r) },
    );
  }

  function reset() {
    setResult(null);
    setEmail("");
    setBusinessName("");
    setContactName("");
    setPhone("");
    invite.reset();
  }

  return (
    <Card title="Invite someone new">
      {result ? (
        <div className="space-y-2 text-sm">
          <MagicLinkPanel result={result} />
          <button type="button" onClick={reset} className="text-sm font-semibold text-brand-700">
            Invite another contractor
          </button>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="invite-email" className="block text-xs font-medium text-charcoal">
                Business email (required)
              </label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="pro@business.com"
                className={`mt-1 ${FIELD_CLASS}`}
              />
            </div>
            <div>
              <label htmlFor="invite-business" className="block text-xs font-medium text-charcoal">
                Business name (for new contractors)
              </label>
              <input
                id="invite-business"
                value={businessName}
                maxLength={200}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Plumbing LLC"
                className={`mt-1 ${FIELD_CLASS}`}
              />
            </div>
            <div>
              <label htmlFor="invite-contact" className="block text-xs font-medium text-charcoal">
                Contact name (optional)
              </label>
              <input
                id="invite-contact"
                value={contactName}
                maxLength={200}
                onChange={(e) => setContactName(e.target.value)}
                className={`mt-1 ${FIELD_CLASS}`}
              />
            </div>
            <div>
              <label htmlFor="invite-phone" className="block text-xs font-medium text-charcoal">
                Phone (optional)
              </label>
              {/* Mirrors the server's zod bounds so a short phone is caught
                  at the keyboard, not as an opaque 400. */}
              <input
                id="invite-phone"
                type="tel"
                minLength={7}
                maxLength={32}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`mt-1 ${FIELD_CLASS}`}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={invite.isPending || email.trim().length === 0}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Generate link &amp; send invite
          </button>
          {invite.error && <p className="text-sm text-rose-700">{errorMessage(invite.error)}</p>}
        </form>
      )}
    </Card>
  );
}

/**
 * Photos, read-only for everyone and editable in triage. Renders even with no
 * photos when the staffer can edit — customers routinely email photos to
 * support instead of uploading them, and this is where those land.
 */
function PhotosCard({
  rfqId,
  images,
  canEdit,
}: {
  rfqId: string;
  images: RfqImage[];
  canEdit: boolean;
}) {
  const remove = useRfqImageDelete(rfqId);
  const { uploads, isPending, upload, dismiss } = useRfqImageUpload(rfqId);
  // Removal is one click from irreversible, so it takes two.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  return (
    <Card title="Photos">
      {images.length === 0 ? (
        <p className="text-sm text-charcoal">No photos yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {images.map((img) =>
            canEdit ? (
              <div key={img.id} className="w-24">
                <a href={img.url} target="_blank" rel="noreferrer">
                  <img src={img.url} alt="RFQ" className="h-24 w-24 rounded-lg object-cover" />
                </a>
                {confirmingId === img.id ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        remove.mutate(img.id);
                        setConfirmingId(null);
                      }}
                      className="rounded-lg bg-rose-700 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Yes, remove
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingId(null)}
                      className="rounded-lg border border-charcoal/30 px-2 py-1 text-xs font-semibold text-charcoal"
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(img.id)}
                    className="mt-1 w-full rounded-lg border border-charcoal/30 px-2 py-1 text-xs font-semibold text-charcoal"
                  >
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                <img src={img.url} alt="RFQ" className="h-24 w-24 rounded-lg object-cover" />
              </a>
            ),
          )}
        </div>
      )}

      {canEdit && (
        <div className="mt-3 space-y-2">
          <label className="flex min-h-[44px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-charcoal/30 px-4 text-sm font-semibold text-brand-700 sm:inline-flex">
            Add photos
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={isPending}
              className="sr-only"
              onChange={(e) => {
                void upload(e.target.files);
                // Reset so re-selecting the same file fires onChange again.
                e.target.value = "";
              }}
            />
          </label>

          {uploads.length > 0 && (
            <ul className="space-y-1 text-xs">
              {uploads.map((u) => (
                <li key={u.id} className={u.status === "error" ? "text-rose-700" : "text-charcoal"}>
                  {u.name} — {uploadLabel(u.status, u.progress, u.error)}
                </li>
              ))}
            </ul>
          )}
          {uploads.length > 0 && !isPending && (
            <button
              type="button"
              onClick={dismiss}
              className="text-xs font-semibold text-brand-700"
            >
              Clear list
            </button>
          )}
          {remove.error && <p className="text-sm text-rose-700">{errorMessage(remove.error)}</p>}
        </div>
      )}
    </Card>
  );
}

function uploadLabel(status: string, progress: number, error?: string): string {
  if (status === "done") return "added";
  if (status === "error") return error ?? "failed";
  return `${Math.round(progress * 100)}%`;
}

function priceOf(q: QuoteProjection): string {
  const quote = q.quote;
  if (!quote) return "—";
  if (quote.type === "fixed" && quote.amount) return centsToDollars(quote.amount.amountCents);
  if (quote.type === "range" && quote.minAmount && quote.maxAmount) {
    return `${centsToDollars(quote.minAmount.amountCents)}–${centsToDollars(quote.maxAmount.amountCents)}`;
  }
  return "site visit";
}

/**
 * Both halves of the post-job feedback, side by side.
 *
 * Neither party ever sees the other's: the homeowner's rating is invisible to
 * the pro, and the pro's assessment of the homeowner is invisible to them.
 * That asymmetry is deliberate — it is what makes either answer candid — and
 * this card is the only place the two meet.
 *
 * A bad outcome is called out rather than left as a number to notice: an
 * unrated job that never happened is the one a staffer has to act on today.
 */
function FeedbackCard({
  feedback,
  jobs,
}: {
  feedback: RfqDetailData["feedback"];
  jobs: JobProjection[];
}) {
  const proFeedback = jobs.map((j) => j.contractorFeedback).find(Boolean);
  // Show the card as soon as the job completes, not only once somebody
  // answers. A completed job means both asks have gone out, and "asked, no
  // reply yet" is exactly the state a staffer needs to see — hiding the card
  // until a reply arrives leaves them with nowhere to look while waiting, and
  // no way to tell "nobody answered" from "we never asked".
  const asked = jobs.some((j) => j.status === "COMPLETED");
  if (!feedback && !proFeedback && !asked) return null;

  const needsAttention = feedback && (!feedback.workCompleted || (feedback.rating ?? 5) <= 2);

  return (
    <Card title="Feedback">
      {needsAttention && (
        <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800">
          {feedback.workCompleted
            ? "Low rating — worth a call."
            : "The customer says this work never got done."}
        </p>
      )}

      <div className="space-y-3 text-sm">
        <div>
          <p className="font-semibold text-charcoal">
            Customer &rarr; pro{" "}
            {feedback?.source === "staff" && (
              <span className="font-normal text-charcoal">(taken by staff)</span>
            )}
          </p>
          {feedback ? (
            <>
              <p className="text-charcoal">
                {feedback.workCompleted
                  ? `${stars(feedback.rating)} · work completed`
                  : "Reported NOT completed"}
              </p>
              {feedback.comment && (
                <p className="mt-1 whitespace-pre-wrap text-charcoal">
                  &ldquo;{feedback.comment}&rdquo;
                </p>
              )}
            </>
          ) : (
            <p className="text-charcoal">No response yet.</p>
          )}
        </div>

        <div className="border-t border-charcoal/10 pt-3">
          <p className="font-semibold text-charcoal">Pro &rarr; customer</p>
          {proFeedback ? (
            <>
              <p className="text-charcoal">
                {stars(proFeedback.rating)} ·{" "}
                {proFeedback.wouldWorkAgain
                  ? "would work with them again"
                  : "would NOT work with them again"}
              </p>
              {proFeedback.comment && (
                <p className="mt-1 whitespace-pre-wrap text-charcoal">
                  &ldquo;{proFeedback.comment}&rdquo;
                </p>
              )}
            </>
          ) : (
            <p className="text-charcoal">No response yet.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function stars(rating: number | undefined): string {
  if (rating === undefined) return "Not rated";
  return `${"\u2605".repeat(rating)}${"\u2606".repeat(5 - rating)} (${rating}/5)`;
}

/**
 * The quotes on the request, and — for an admin — the ability to accept one
 * FOR the customer.
 *
 * Why staff can do this at all: a customer who agreed on the phone often
 * never goes back to the app to press the button, and until they do there is
 * no job, so the contractor has no controls and the request sits on "quoted"
 * forever. Accepting here is the same authoritative act, not a shortcut
 * around it — the customer system runs identical checks and fires the same
 * contact release.
 *
 * Because it acts for someone else and cannot be undone from this screen,
 * it is gated three ways: admin only, a confirm step that spells out what
 * gets shared, and a reason recorded on the request's triage notes.
 */
function QuotesCard({
  rfqId,
  quotes,
  selection,
  canAccept,
}: {
  rfqId: string;
  quotes: QuoteProjection[];
  selection: RfqDetailData["selection"];
  canAccept: boolean;
}) {
  const selectQuote = useRfqSelectQuote(rfqId);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  return (
    <Card title={`Quotes (${quotes.length})`}>
      {quotes.length === 0 ? (
        <p className="text-sm text-charcoal">No quotes yet.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {quotes.map((q) => (
            <li key={q.quoteId} className="border-b border-charcoal/10 pb-3 last:border-0 last:pb-0">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-charcoal">
                  {q.contractorProfile?.businessName ?? "Contractor"} — {priceOf(q)}
                  {q.status === "WITHDRAWN" && (
                    <span className="font-normal text-charcoal"> (withdrawn)</span>
                  )}
                </span>
                {selection?.quoteId === q.quoteId && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                    {selection.status === "VOIDED" ? "selected (voided)" : "selected"}
                  </span>
                )}
              </div>
              {/* The pro's own caveats and the dates they can actually come.
                  Both were already on this page's payload and unrendered —
                  they are what a staffer advising a customer needs most. */}
              {q.quote?.notes && (
                <p className="mt-1 whitespace-pre-wrap text-charcoal">{q.quote.notes}</p>
              )}
              {(q.quote?.availabilityWindows ?? []).length > 0 && (
                <p className="mt-1 text-xs text-charcoal">
                  Available: {q.quote!.availabilityWindows!.map((w) => formatWindow(w)).join(" · ")}
                </p>
              )}
              {selection?.quoteId === q.quoteId && selection.selectedBy && (
                <p className="mt-1 text-xs text-charcoal">
                  Accepted for the customer by {selection.selectedBy}
                </p>
              )}

              {canAccept && q.status === "ACTIVE" && (
                <div className="mt-2">
                  {confirming === q.quoteId ? (
                    <div className="space-y-2 rounded-xl bg-field-gray p-3">
                      <p className="text-charcoal">
                        Accept this quote for the customer? Their name, address, and contact details
                        will be shared with{" "}
                        {q.contractorProfile?.businessName ?? "this contractor"}, and the other
                        invited pros will be told the job is filled. This can&rsquo;t be undone
                        here.
                      </p>
                      <label className="block">
                        <span className="text-xs font-semibold text-charcoal">
                          Why (recorded on the request)
                        </span>
                        <input
                          type="text"
                          value={reason}
                          maxLength={500}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="e.g. customer confirmed by phone"
                          className="mt-1 w-full rounded-lg border border-charcoal/20 px-3 py-2 text-sm"
                        />
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={selectQuote.isPending}
                          onClick={() =>
                            selectQuote.mutate(
                              {
                                quoteId: q.quoteId,
                                ...(reason.trim() ? { reason: reason.trim() } : {}),
                              },
                              {
                                onSuccess: () => {
                                  setConfirming(null);
                                  setReason("");
                                },
                              },
                            )
                          }
                          className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                        >
                          {selectQuote.isPending ? "Accepting…" : "Yes, accept for customer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirming(null)}
                          className="rounded-lg border border-charcoal/20 px-3 py-2 text-sm font-semibold text-charcoal"
                        >
                          Cancel
                        </button>
                      </div>
                      {selectQuote.error && (
                        <p className="text-sm text-rose-700">{errorMessage(selectQuote.error)}</p>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setConfirming(q.quoteId);
                        setReason("");
                      }}
                      className="rounded-lg border border-brand-700 px-3 py-1.5 text-xs font-semibold text-brand-700"
                    >
                      Accept for customer
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Find a pro for this request — the console's matching hub.
 *
 * Two questions, one card. Left alone it answers the one the system can
 * answer itself: which APPROVED pros cover this category and ZIP, from the
 * identical `MATCH#` query the automatic fan-out runs, so a staffer choosing
 * by hand sees exactly the set the system would have picked — and an empty
 * list is a diagnosis, not a dead end.
 *
 * Typing answers the one it can't. Matching is a category-and-ZIP lookup, and
 * some of the best calls a staffer makes step outside it — a roofing company
 * owns the tall ladder a second-floor job needs, and no amount of category
 * matching will ever suggest them for a handyman request. Search reaches the
 * whole registry, whatever a pro is listed under, and says on each row how
 * far outside this job they sit. The heading always names which of the two
 * lists is on screen.
 *
 * Every invite from here takes a second click, including an in-category one.
 * An invitation is not a note to self: it emails a business a link to the
 * customer's description, ZIP and photos, and re-inviting someone silently
 * kills the link already sitting in their inbox. The confirm step says which
 * of those two things is about to happen, and offers the one line of context
 * that stops an off-category invite reading as a mistake.
 */
function FindProCard({
  rfqId,
  category,
  postalCode,
  photoCount,
  canInvite,
}: {
  rfqId: string;
  category: string;
  postalCode: string;
  photoCount: number;
  canInvite: boolean;
}) {
  const [term, setTerm] = useState("");
  const search = useDebounced(term.trim(), SEARCH_DEBOUNCE_MS);
  const searching = search.length >= MIN_SEARCH_LENGTH;

  // Left enabled while searching so clearing the box is instant rather than a
  // second round trip — it is a cached read of a list the page already showed.
  const matches = useRfqMatches(rfqId, category, postalCode);
  const found = useContractorSearch(search);
  // Already loaded for the Invited card under the same key prefix; joining it
  // in is what turns "Invite" into "Resend link" before the click, instead of
  // after, when the pro's previous link is already dead.
  const invitations = useRfqInvitations(rfqId);

  const invite = useRfqInvite(rfqId);
  const [confirming, setConfirming] = useState<{ contractorId: string; note: string } | null>(null);
  const [invited, setInvited] = useState<{
    contractorId: string;
    result: InviteContractorResult;
  } | null>(null);

  const active = searching ? found : matches;
  const rows = sortForPicking(searching ? (found.data?.items ?? []) : (matches.data?.items ?? []));
  const invitedBefore = new Map(
    (invitations.data?.items ?? []).map((inv) => [inv.contractorId, inv]),
  );

  function send(pro: ProCandidate, note: string) {
    setConfirming(null);
    // By id, never by email: the id is always present, and a picked row means
    // one specific registry record — resolving it again through the address
    // is a second chance to land on the wrong one.
    invite.mutate(
      { contractorId: pro.contractorId, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        // Guarded because the panel below renders the link unconditionally,
        // and a 200 whose body isn't the shape we expect would take the whole
        // console down mid-invite — with the plaintext token, which is only
        // ever returned once, going down with it.
        onSuccess: (result) => {
          if (result?.invitation?.link) setInvited({ contractorId: pro.contractorId, result });
        },
      },
    );
  }

  return (
    <Card title="Find a pro">
      <label htmlFor="pro-search" className="block text-xs font-medium text-charcoal">
        Search all pros — any category
      </label>
      <input
        id="pro-search"
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Business name, contact, email, phone or ZIP"
        className={`mt-1 ${FIELD_CLASS}`}
      />

      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-charcoal">
        {searching
          ? `Registry matches for “${search}”`
          : `Suggested — ${categoryLabel(category)} · ${postalCode}`}
      </p>

      {active.isPending || active.isFetching ? (
        <p className="mt-2 text-sm text-charcoal">
          {searching ? "Searching the registry…" : "Looking for approved pros…"}
        </p>
      ) : active.isError ? (
        <p className="mt-2 text-sm text-charcoal">
          {searching ? "Couldn’t search the registry." : "Couldn’t load matches."}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-sm text-charcoal">
          {searching ? (
            <>No pro in the registry matches “{search}”. Invite them by email below.</>
          ) : (
            <>
              No approved pros cover {categoryLabel(category)} in {postalCode} yet. Search above for
              a pro in another category, or invite one by email below.
            </>
          )}
        </p>
      ) : (
        <ul className="mt-2 space-y-3 text-sm">
          {rows.map((pro) => {
            const cover = coverage(pro, category, postalCode);
            const already = invitedBefore.get(pro.contractorId);
            const showing = invited?.contractorId === pro.contractorId ? invited.result : null;
            const isConfirming = confirming?.contractorId === pro.contractorId;
            const rowError =
              invite.error && invite.variables?.contractorId === pro.contractorId
                ? errorMessage(invite.error)
                : null;

            return (
              <li
                key={pro.contractorId}
                className="border-b border-charcoal/10 pb-3 last:border-0 last:pb-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/contractors/${pro.contractorId}`}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      {pro.businessName}
                    </Link>
                    <div className="text-xs text-charcoal">
                      {[pro.contactName, pro.phone, pro.email].filter(Boolean).join(" · ")}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1 text-xs">
                      {pro.status !== "APPROVED" && (
                        <Pill tone="slate">{statusLabel(pro.status)}</Pill>
                      )}
                      {cover.matched ? (
                        // Describes the PROFILE, not the matcher: this is
                        // computed from the pro's own categories and ZIPs,
                        // and a stale MATCH# row can make the two disagree.
                        <Pill tone="emerald">listed for this category and ZIP</Pill>
                      ) : (
                        <Pill tone="amber">{cover.reason}</Pill>
                      )}
                      {already && (
                        <Pill tone="slate">
                          invited {new Date(already.createdAt).toLocaleDateString()}
                        </Pill>
                      )}
                      {pro.emailSuppressed && (
                        <Pill tone="amber">email bounces — share the link yourself</Pill>
                      )}
                    </div>
                    {pro.serviceCategories.length > 0 && (
                      <p className="mt-1 text-xs text-charcoal">
                        Listed under {pro.serviceCategories.map(categoryLabel).join(", ")}
                        {(pro.serviceArea?.postalCodes ?? []).length > 0 &&
                          ` · ${pro.serviceArea!.postalCodes.join(", ")}`}
                      </p>
                    )}
                    {(pro.services ?? []).length > 0 && (
                      // The pro's own words. On an off-category pick this is
                      // the line that actually answers "would they take it?"
                      <p className="mt-1 text-xs text-charcoal">
                        Says they do: {pro.services!.join(", ")}
                      </p>
                    )}
                  </div>

                  {canInvite && !showing && !isConfirming && (
                    <div className="flex shrink-0 items-center gap-2">
                      {pro.status === "SUSPENDED" ? (
                        <span className="text-xs text-charcoal">
                          Suspended — reinstate before inviting
                        </span>
                      ) : (
                        <button
                          type="button"
                          disabled={invite.isPending}
                          onClick={() =>
                            setConfirming({ contractorId: pro.contractorId, note: "" })
                          }
                          className="rounded-lg border border-charcoal/30 px-3 py-1.5 text-sm font-semibold text-charcoal disabled:opacity-50"
                        >
                          {already ? "Resend link" : "Invite"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {isConfirming && (
                  <div className="mt-2 rounded-xl bg-field-gray p-3">
                    <p className="text-sm text-charcoal">
                      {already ? (
                        <>
                          <strong>{pro.businessName}</strong> already holds a link to this request.
                          Sending again emails them a new one and{" "}
                          <strong>stops the old link working</strong>.
                        </>
                      ) : (
                        <>
                          Emails <strong>{pro.businessName}</strong> a link to this request — the
                          description, {postalCode}, and{" "}
                          {photoCount === 1 ? "1 photo" : `${photoCount} photos`}.
                        </>
                      )}
                      {pro.status !== "APPROVED" && (
                        <>
                          {" "}
                          They are <strong>{statusLabel(pro.status)}</strong>, not an approved pro.
                        </>
                      )}
                    </p>
                    <label
                      htmlFor={`why-${pro.contractorId}`}
                      className="mt-2 block text-xs font-medium text-charcoal"
                    >
                      Why you picked them (optional — they see this)
                    </label>
                    <input
                      id={`why-${pro.contractorId}`}
                      value={confirming.note}
                      maxLength={500}
                      onChange={(e) =>
                        setConfirming((c) => (c ? { ...c, note: e.target.value } : c))
                      }
                      placeholder={
                        cover.matched
                          ? "e.g. you quoted a similar job last month"
                          : "e.g. you have the ladder for a second-floor job"
                      }
                      className={`mt-1 ${FIELD_CLASS}`}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={invite.isPending}
                        onClick={() => send(pro, confirming.note)}
                        className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        {already ? "Send a new link" : "Send invite"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(null)}
                        className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal"
                      >
                        {/* Not "Cancel": this page already has a Cancel that
                            cancels the customer's whole request. */}
                        Don&rsquo;t send
                      </button>
                    </div>
                  </div>
                )}

                {showing && (
                  <div className="mt-2">
                    <MagicLinkPanel result={showing} />
                  </div>
                )}
                {rowError && <p className="mt-2 text-sm text-rose-700">{rowError}</p>}
              </li>
            );
          })}
        </ul>
      )}

      {searching && found.data?.truncated && (
        <p className="mt-2 text-xs text-charcoal">
          Showing the first {rows.length} matches — narrow the search to see the rest.
        </p>
      )}
    </Card>
  );
}

/** The shape both the match query and the registry search can fill. */
interface ProCandidate {
  contractorId: string;
  businessName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  status: string;
  emailSuppressed: boolean;
  serviceCategories: string[];
  services?: string[];
  serviceArea?: { postalCodes: string[]; cities: string[] };
}

/**
 * Approved pros first, suspended last, alphabetical within each band. Search
 * reaches applicants mid-review and intake leads nobody has spoken to, which
 * is deliberate — a manually created LEAD is only ever reachable this way —
 * but they should never be the first thing a scanning eye lands on.
 */
function sortForPicking(items: ProCandidate[]): ProCandidate[] {
  const rank = (s: string) => (s === "APPROVED" ? 0 : s === "SUSPENDED" ? 2 : 1);
  return [...items].sort(
    (a, b) => rank(a.status) - rank(b.status) || a.businessName.localeCompare(b.businessName),
  );
}

/** ZIPs arrive as 5-digit, ZIP+4 and the occasional stray format. */
function zip5(value: string | undefined): string {
  return (value ?? "").replace(/\D/g, "").slice(0, 5);
}

/**
 * How far outside this job a pro sits, and the shortest true sentence for it.
 * Only APPROVED pros hold match entries, so an unapproved pro never "covers"
 * a job however their profile reads — the automatic fan-out would skip them.
 */
function coverage(
  pro: ProCandidate,
  category: string,
  postalCode: string,
): { matched: boolean; reason: string } {
  const inCategory = pro.serviceCategories.includes(category);
  const inArea = (pro.serviceArea?.postalCodes ?? []).some((p) => zip5(p) === zip5(postalCode));
  if (pro.status !== "APPROVED") return { matched: false, reason: "not approved for matching" };
  if (!inCategory) return { matched: false, reason: `not listed for ${categoryLabel(category)}` };
  if (!inArea) return { matched: false, reason: `doesn’t cover ${postalCode}` };
  return { matched: true, reason: "" };
}

function statusLabel(status: string): string {
  if (status === "PENDING_REVIEW") return "in review";
  return status.toLowerCase();
}

function Pill({
  tone,
  children,
}: {
  tone: "emerald" | "amber" | "slate";
  children: React.ReactNode;
}) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    slate: "bg-field-gray text-charcoal",
  } as const;
  return (
    <span className={`rounded-full px-2 py-0.5 font-semibold ${tones[tone]}`}>{children}</span>
  );
}

/**
 * The freshly minted magic link, shown once. Both invite paths land here: the
 * link is the whole product of an invite, and it is unrecoverable afterwards
 * — the plaintext token is never stored, so re-inviting is the only way to
 * get another one, and that kills the link already in the pro's inbox.
 */
function MagicLinkPanel({ result }: { result: InviteContractorResult }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="space-y-2 text-sm">
      <p className="text-charcoal">
        {result.invitation.emailSent ? (
          <>
            Invitation emailed to <strong>{result.contractor.businessName}</strong>.
          </>
        ) : (
          <>
            <strong>{result.contractor.businessName}</strong> has email suppressed (earlier
            bounce/complaint) — share the link with them yourself.
          </>
        )}
        {!result.invitation.created && (
          <> They were already invited; this replaces their previous link.</>
        )}
      </p>
      <div className="flex gap-2">
        <input
          readOnly
          value={result.invitation.link}
          onFocus={(e) => e.target.select()}
          className={`flex-1 ${FIELD_CLASS}`}
          aria-label="Magic link"
        />
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(result.invitation.link).then(() => {
              setCopied(true);
            });
          }}
          className="rounded-lg border border-charcoal/30 px-3 py-2 text-sm font-semibold text-charcoal"
        >
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <p className="text-xs text-charcoal">
        Link expires {new Date(result.invitation.expiresAt).toLocaleDateString()}. The pro can open
        it and quote — no account needed.
      </p>
    </div>
  );
}

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Trailing debounce, so a fast typist fires one search instead of eight. Each
 * search is a signed hop through two APIs onto a registry-wide read, and the
 * contractor API's throttle is stage-wide — shared with the magic-link routes
 * pros are using at the same moment.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/**
 * Who has already been asked to quote, and what came of it.
 *
 * The quotes card only ever showed pros who replied, so "invited four, heard
 * nothing" looked identical to "nobody invited yet" — and staff re-invited
 * people who already held a live link.
 */
function InvitedCard({ rfqId }: { rfqId: string }) {
  const { data, isPending, isError } = useRfqInvitations(rfqId);
  const items = data?.items ?? [];

  if (isPending || isError || items.length === 0) return null;

  return (
    <Card title={`Invited (${items.length})`}>
      <ul className="space-y-2 text-sm">
        {items.map((inv) => (
          <li key={inv.invitationId} className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <Link
                to={`/contractors/${inv.contractorId}`}
                className="font-medium text-brand-700 hover:underline"
              >
                {inv.businessName}
              </Link>
              <div className="text-xs text-charcoal">
                Invited {new Date(inv.createdAt).toLocaleDateString()}
                {inv.notifiedAt ? "" : " · email not sent"}
                {inv.pendingQuestion && ` · asked: ${inv.pendingQuestion}`}
              </div>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${invitationStyles(inv)}`}
            >
              {invitationLabel(inv)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Expiry and revocation are what a staffer acts on, so they outrank the raw
 * status: a QUOTED invitation whose link has lapsed is still a quote, but an
 * INVITED one that lapsed is a dead end worth re-sending.
 */
function invitationLabel(inv: RfqInvitationSummary): string {
  // Read revocation from the STATUS, never from the presence of revokedAt: an
  // invitation that was revoked and later reinstated keeps its old timestamp,
  // and reading that as "revoked" reports a pro who can work as locked out.
  if (inv.status === "REVOKED") return "revoked";
  if (inv.status === "QUOTED") return "quoted";
  if (inv.expired) return "link expired";
  if (inv.status === "VIEWED") return "viewed";
  return "no response";
}

function invitationStyles(inv: RfqInvitationSummary): string {
  if (inv.status === "QUOTED") return "bg-emerald-100 text-emerald-800";
  if (inv.status === "REVOKED" || inv.expired) return "bg-rose-100 text-rose-700";
  if (inv.status === "VIEWED") return "bg-amber-100 text-amber-800";
  return "bg-field-gray text-charcoal";
}

/**
 * The customer, reachable in one tap.
 *
 * Email and phone are links, not text: a staffer on this screen is almost
 * always about to call or write. When the Cognito lookup comes back empty the
 * card still shows the customer's sub — the notification email has always
 * included it, and without it there is no handle at all to find the person.
 */
function ContactCard({
  customer,
  customerSub,
  contactName,
}: {
  customer: CustomerContact | undefined;
  customerSub: string;
  contactName: string;
}) {
  return (
    <Card title="Customer">
      {customer ? (
        <dl className="space-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-charcoal">Name</dt>
            <dd className="font-medium text-charcoal">{contactName || "(not given)"}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-charcoal">Email</dt>
            <dd>
              {customer.email ? (
                <a
                  href={`mailto:${customer.email}`}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {customer.email}
                </a>
              ) : (
                <span className="text-charcoal">(not given)</span>
              )}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-charcoal">Phone</dt>
            <dd>
              {customer.phoneNumber ? (
                <a
                  href={`tel:${customer.phoneNumber}`}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {customer.phoneNumber}
                </a>
              ) : (
                <span className="text-charcoal">(not given)</span>
              )}
            </dd>
          </div>
        </dl>
      ) : (
        <div className="text-sm text-charcoal">
          <p>Contact lookup unavailable.</p>
          <p className="mt-1 text-xs">
            Customer id <span className="font-mono break-all">{customerSub}</span>
          </p>
        </div>
      )}
    </Card>
  );
}

/**
 * Where the job actually is. City and ZIP are enough to match a request to a
 * pro; only the street line is enough to send one, and that is the whole
 * point of this console.
 */
function JobLocationCard({ location }: { location: RfqLocation }) {
  const line1 = [location.street, location.unit].filter(Boolean).join(", ");
  const line2 = `${location.city}, ${location.state} ${location.postalCode}`;
  const mapsQuery = encodeURIComponent([line1, line2].filter(Boolean).join(", "));

  return (
    <Card title="Job location">
      <div className="text-sm text-charcoal">
        {line1 ? (
          <p className="font-medium">{line1}</p>
        ) : (
          <p className="font-medium">(no street address on file)</p>
        )}
        <p>{line2}</p>
        {line1 && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs font-semibold text-brand-700 hover:underline"
          >
            Open in Maps
          </a>
        )}
      </div>
    </Card>
  );
}

/** Staff-facing category/urgency labels; falls back to the raw value. */
function categoryLabel(value: string): string {
  return SERVICE_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

function urgencyLabel(value: string): string {
  return URGENCIES.find((u) => u.value === value)?.label ?? value;
}

function formatWindow(w: { start: string; end: string }): string {
  const start = new Date(w.start);
  const end = new Date(w.end);
  const sameDay = start.toDateString() === end.toDateString();
  return sameDay
    ? `${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}–${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
    : `${start.toLocaleDateString()}–${end.toLocaleDateString()}`;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 rounded-2xl border border-charcoal/15 bg-white p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function BackLink() {
  return (
    <Link to="/customers" className="text-sm font-semibold text-brand-700">
      &larr; Customer requests
    </Link>
  );
}
